import { useStripe } from '@stripe/react-stripe-js';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';

import { ReviewLayout } from '../../../../components/Checkout/Review';
import { useCheckoutCtx } from '../../../../providers/CheckoutProvider';
import { CHECKOUT_INTENT_PARAM } from '../../hooks/useCheckoutRedirectReturn';
import { shouldAuthorizePayableAmount } from '../../utils/fundLock';
import {
  isStripeErrorKey,
  resolveStripeErrorKey,
} from '../../utils/stripeErrors';
import { CheckoutElementsProvider } from '../CheckoutElementsProvider';

const RESALE_AGREEMENT_HREF = 'https://avantarte.com/support/resale-agreement';

const ReviewContent = () => {
  const {
    allowMultipleSelections,
    checkoutLoading,
    clientSecret,
    confirmEntry,
    drawCloseAt,
    framingDialog,
    framingSelections,
    intentId,
    isLinkedDraw,
    isPreOrderFlow,
    maxQuantity,
    orderTotals,
    orderedItems,
    redirectReturn,
    step,
    submitError,
    summaryProps,
    trackEntrySuccess,
  } = useCheckoutCtx();
  const stripe = useStripe();
  const { t } = useTranslation('checkout');
  const [submitErrorKey, setSubmitErrorKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const copyKey = isPreOrderFlow ? 'review.preOrder' : 'review.draw';
  const showCardHold =
    isLinkedDraw &&
    shouldAuthorizePayableAmount({
      drawCloseAt,
      preOrder: isPreOrderFlow,
    });

  const resolveError = () => {
    if (submitError) return t('review.errors.update');
    if (submitErrorKey) return t(submitErrorKey);
    if (redirectReturn.status === 'failed') {
      return t('review.errors.confirmation');
    }

    return undefined;
  };

  const resolveErrorAction = () => {
    if (submitError || !submitErrorKey || !isStripeErrorKey(submitErrorKey)) {
      return undefined;
    }

    return {
      label: t('stripeErrors.updateCard'),
      onClick: () => step.goToStep('details'),
    };
  };

  const handleSubmit = async () => {
    setSubmitErrorKey(null);

    if (!stripe || !clientSecret || !intentId || submitError) {
      setSubmitErrorKey('review.errors.generic');
      return;
    }

    setSubmitting(true);
    try {
      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set('step', 'success');
      returnUrl.searchParams.set(CHECKOUT_INTENT_PARAM, intentId);

      const confirmParams = { return_url: returnUrl.toString() };
      const result = clientSecret.startsWith('seti_')
        ? await stripe.confirmSetup({
            clientSecret,
            confirmParams,
            redirect: 'if_required',
          })
        : await stripe.confirmPayment({
            clientSecret,
            confirmParams,
            redirect: 'if_required',
          });

      if (result.error) {
        setSubmitErrorKey(resolveStripeErrorKey(result.error));
        return;
      }

      const confirmed = await confirmEntry();

      if (!confirmed) {
        setSubmitErrorKey('review.errors.confirmation');
        return;
      }

      trackEntrySuccess();
      step.goNext();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ReviewLayout
      cardHold={
        showCardHold
          ? {
              description: t(`${copyKey}.cardHoldDescription`),
              title: t('review.cardHold.title'),
            }
          : undefined
      }
      currencyCode={summaryProps.currencyCode}
      disclaimer={t(`${copyKey}.disclaimer`)}
      error={resolveError()}
      errorAction={resolveErrorAction()}
      framingSelections={framingSelections}
      freeShipping={orderTotals.freeShipping}
      isEstimate={orderTotals.isEstimate}
      isSubmitting={submitting || checkoutLoading}
      maxOrderValue={orderTotals.maxOrderValue}
      maxQuantity={allowMultipleSelections ? orderedItems.length : maxQuantity}
      maxQuantityNote={
        isLinkedDraw
          ? {
              allocation: isPreOrderFlow
                ? undefined
                : t('review.draw.allocationNote'),
              label: t(`${copyKey}.maxWorks`),
            }
          : undefined
      }
      onAddFrame={framingDialog.openFor}
      onSubmit={() => void handleSubmit()}
      orderValue={orderTotals.orderValue}
      orderedItems={orderedItems}
      resaleAgreementHref={RESALE_AGREEMENT_HREF}
      shippingCost={orderTotals.shippingCost}
      showPreference={isLinkedDraw}
      taxRate={orderTotals.taxRate}
      taxValue={orderTotals.taxValue}
      submitLabel={t('review.submit')}
      title={t(`${copyKey}.title`)}
    />
  );
};

export const Review = () => (
  <CheckoutElementsProvider>
    <ReviewContent />
  </CheckoutElementsProvider>
);
